import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useI18n } from '../../lib/i18n';
import { navigate } from '../../lib/router';
import { AsyncButton, Badge, Button, Card, errorText, Field, PageHeader, Select, Textarea, useToast } from '../../ui/components';

const SOURCES = ['fitmanager', 'virtuagym', 'mindbody', 'generic'];

/** Bringing members over from the gym's previous software: read the file, check it, then write. */
export function ImportPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const [source, setSource] = useState('fitmanager');
  const [preview, setPreview] = useState<any | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState('');

  const check = async (text = csv, src = source): Promise<void> => {
    setResult(null);
    setError('');
    try {
      setPreview(await api(`${T(gym.slug)}/admin/import/preview`, { body: { csv: text, source: src } }));
    } catch (e) {
      setPreview(null);
      setError(errorText(e, i18n));
    }
  };

  return (
    <div className="page">
      <PageHeader title={i18n.t('import.title')} subtitle={i18n.t('import.subtitle')} />
      <Card>
        <div className="grid-2">
          <Field label={i18n.t('import.source')} hint={i18n.t('import.source_hint')}>
            <Select value={source} onChange={(e) => { setSource(e.target.value); if (csv) void check(csv, e.target.value); }}
              options={SOURCES.map((s) => ({ value: s, label: i18n.t(`import.source_${s}`) }))} />
          </Field>
          <Field label={i18n.t('import.file')}>
            <input type="file" accept=".csv,text/csv" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const text = await f.text();
              setCsv(text);
              void check(text);
            }} />
          </Field>
        </div>
        <Field label={i18n.t('import.paste')} hint={i18n.t('import.paste_hint')}>
          <Textarea rows={5} value={csv.slice(0, 5000)} onChange={(e) => setCsv(e.target.value)} />
        </Field>
        <Button variant="secondary" disabled={!csv.trim()} onClick={() => check()}>{i18n.t('import.check')}</Button>
        {error && <p className="small warn">{error}</p>}
      </Card>

      {preview && (
        <>
          <Card title={i18n.t('import.mapping')}>
            <p className="small muted">{i18n.t('import.mapping_hint')}</p>
            <div className="grid-2">
              {preview.columns.map((c: string) => (
                <Field key={c} label={c}>
                  <Select value={preview.mapping[c] ?? ''} placeholder={i18n.t('import.skip')}
                    onChange={(e) => setPreview({ ...preview, mapping: { ...preview.mapping, [c]: e.target.value } })}
                    options={preview.fields.map((f: string) => ({ value: f, label: i18n.field(f) }))} />
                </Field>
              ))}
            </div>
          </Card>
          <Card title={i18n.t('import.result')}>
            <div className="stats">
              <div className="stat"><div className="stat-label">{i18n.t('import.new')}</div><div className="stat-value">{preview.willCreate}</div></div>
              <div className="stat"><div className="stat-label">{i18n.t('import.existing')}</div><div className="stat-value">{preview.willUpdate}</div></div>
              <div className="stat"><div className="stat-label">{i18n.t('import.skipped')}</div><div className="stat-value">{preview.problems.length}</div></div>
            </div>
            {preview.problems.length > 0 && (
              <ul className="sets">
                {preview.problems.slice(0, 10).map((p: any) => (
                  <li key={p.line}>
                    {i18n.t('import.line', { line: p.line })}{p.name ? ` · ${p.name}` : ''} · {p.problems.map((x: string) => i18n.t(`import.problem_${x}`)).join(', ')}
                  </li>
                ))}
              </ul>
            )}
            {preview.plans?.length > 0 && (
              <p className="small muted">
                {i18n.t('import.plans')}: {preview.plans.map((p: any) => `${p.name}${p.match ? ' ✓' : ` (${i18n.t('import.plan_unknown')})`}`).join(', ')}
              </p>
            )}
            {!result ? (
              <AsyncButton confirm={i18n.t('import.confirm', { count: preview.willCreate + preview.willUpdate })} onClick={async () => {
                const r = await api(`${T(gym.slug)}/admin/import/run`, { body: { csv, source, mapping: preview.mapping } });
                setResult(r);
                toast.ok(i18n.t('import.done', { created: r.created, updated: r.updated }));
              }}>{i18n.t('import.start')}</AsyncButton>
            ) : (
              <div className="stack">
                <Badge value="done" label={i18n.t('import.done', { created: result.created, updated: result.updated })} />
                <p className="small muted">{i18n.t('import.after', { memberships: result.memberships, tokens: result.tokens })}</p>
                <Button onClick={() => navigate(`${gym.base}/admin/clients`)}>{i18n.t('nav.clients')}</Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
